import { z } from "zod";
import { isValidPhoneNumber } from "libphonenumber-js";
import { formatPhoneNumber, normalizeDateString } from "./formatters";

/**
 * Creates a US phone number validator. Accepts empty strings (use `.min()`
 * to require a value).
 *
 * @param invalidMessage - Message shown for an unparseable/invalid number
 *
 * @example
 * const schema = z.object({
 *   phone: createPhoneNumberSchema(),
 *   requiredPhone: createPhoneNumberSchema().min(1)
 * })
 */
export function createPhoneNumberSchema(
  invalidMessage = "Invalid phone number"
) {
  return z.string().refine(
    (value) => {
      if (!value) {
        return true;
      }
      const parsed = formatPhoneNumber(value);
      if (!parsed) {
        return false;
      }
      return isValidPhoneNumber(parsed, "US");
    },
    { error: invalidMessage }
  );
}

export const PhoneNumberSchema = createPhoneNumberSchema();

/**
 * Creates an email validator using Zod's built-in email validation. Leaves
 * the missing-value message unset so it falls through to the app's
 * registered required-field error map.
 *
 * @param invalidMessage - Message shown for a malformed email address
 *
 * @example
 * const schema = z.object({ email: createEmailSchema() })
 */
export function createEmailSchema(invalidMessage = "Invalid email address") {
  return z.email({
    error: (issue) => (issue.input === undefined ? undefined : invalidMessage)
  });
}

export const EmailSchema = createEmailSchema();

/**
 * Validates date strings in both MM/DD/YYYY and YYYY-MM-DD formats.
 * Properly rejects invalid calendar dates like 04/31/2024 and 2024-02-30.
 * Accepts empty strings, matching the underlying date pickers, which emit
 * "" for a cleared or partially typed date.
 *
 * Automatically normalizes MM/DD/YYYY format (from nine-datepicker) to ISO
 * format before validating with Zod's built-in z.iso.date().
 *
 * @example
 * const schema = z.object({ birthdate: DateStringSchema })
 *
 * // Accepts valid dates in both formats:
 * DateStringSchema.parse("01/15/2024") // ✅
 * DateStringSchema.parse("2024-01-15") // ✅
 * DateStringSchema.parse("") // ✅
 *
 * // Rejects invalid calendar dates:
 * DateStringSchema.parse("04/31/2024") // ❌ April only has 30 days
 * DateStringSchema.parse("2024-02-30") // ❌ February doesn't have 30 days
 */
export const DateStringSchema = z
  .string()
  .transform(normalizeDateString)
  .refine((value) => value === "" || z.iso.date().safeParse(value).success, {
    error: "Invalid date"
  });

/**
 * Creates an OTP/verification code validator (6 digits). Coerces numbers to
 * strings for convenience.
 *
 * @param invalidMessage - Message shown for a code that isn't 6 digits
 *
 * @example
 * const schema = z.object({ code: createOtpSchema() });
 * schema.parse({ code: "123456" }); // ✓
 * schema.parse({ code: 123456 }); // ✓ (coerced)
 * schema.parse({ code: "12345" }); // ✗
 */
export function createOtpSchema(
  invalidMessage = "Code must be exactly 6 digits"
) {
  return z.coerce.string().regex(/^\d{6}$/, { error: invalidMessage });
}

export const OtpSchema = createOtpSchema();
